import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiCall } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface QueueItem {
  title: string;
  artist: string;
  user_name: string;
  status: string;
}

interface UpcomingGig {
  id: number;
  name: string;
  scheduled_date: string | null;
  venue: string | null;
  queue_count: string;
}

const GigHomePage: React.FC = () => {
  const { isLoggedIn, landingPage } = useAuth();
  const [nowPlaying, setNowPlaying] = useState<QueueItem | null>(null);
  const [nextUp, setNextUp] = useState<QueueItem | null>(null);
  const [upcomingGigs, setUpcomingGigs] = useState<UpcomingGig[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await apiCall('/api/songs/queue');
        if (res.ok) {
          const data = await res.json();
          const playing = data.find((item: QueueItem) => item.status === 'playing');
          const queued = data.filter((item: QueueItem) => item.status === 'queued');
          setNowPlaying(playing || queued[0] || null);
          setNextUp(playing ? queued[0] : queued[1] || null);
        }
      } catch {
        // Keep the gig landing page usable if polling fails.
      }

      try {
        const res = await apiCall('/api/songs/gigs/upcoming');
        if (res.ok) setUpcomingGigs(await res.json());
      } catch {
        // Upcoming gigs are helpful, not required.
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <main className="gig-app-home">
      <section className="gig-hero">
        <div className="gig-hero__copy">
          <img className="gig-hero__backdrop" src="/images/photo-2.png" alt="" aria-hidden="true" />
          <img className="gig-hero__logo" src="/images/rodeoke-logo.png" alt="Rodeoke" />
          <p className="eyebrow">Tonight's crowd hub</p>
          <h1>Pick your song and get in the queue.</h1>
          <p>
            Browse the catalogue, add your name, watch the running order, and cheer the singers already up.
          </p>
          <div className="hero-actions">
            <Link className="button-primary" to="/gig/catalogue">
              Choose a song
            </Link>
            <Link className="button-secondary" to="/gig/queue-voting">
              See queue
            </Link>
          </div>
        </div>

        <div className="stage-panel" aria-label="Current stage status">
          <div className="stage-panel__row stage-panel__row--active">
            <span className="stage-label">Now playing</span>
            {nowPlaying ? (
              <>
                <strong>{nowPlaying.title}</strong>
                <span>{nowPlaying.artist}</span>
                <small>Sung by {nowPlaying.user_name}</small>
              </>
            ) : (
              <>
                <strong>Stage warming up</strong>
                <span>The next singer will appear here.</span>
              </>
            )}
          </div>

          <div className="stage-panel__row">
            <span className="stage-label">Next up</span>
            {nextUp ? (
              <>
                <strong>{nextUp.title}</strong>
                <span>{nextUp.artist}</span>
                <small>Sung by {nextUp.user_name}</small>
              </>
            ) : (
              <>
                <strong>Queue is open</strong>
                <span>Add your song before someone else grabs the mic.</span>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="quick-route-grid">
        <Link to="/gig/catalogue" className="quick-route quick-route--primary">
          <span>01</span>
          <strong>Song catalogue</strong>
          <small>Search the list and request your turn.</small>
        </Link>
        <Link to="/gig/nownext" className="quick-route">
          <span>02</span>
          <strong>Now & next</strong>
          <small>Keep tabs on the stage without asking the band.</small>
        </Link>
        <Link to="/gig/queue-voting" className="quick-route">
          <span>03</span>
          <strong>Queue & voting</strong>
          <small>See the running order and rate past performances.</small>
        </Link>
      </section>

      {upcomingGigs.length > 0 && (
        <section className="upcoming-band">
          <div>
            <p className="eyebrow">Coming up</p>
            <h2>Request early for future gigs.</h2>
          </div>
          <div className="upcoming-list">
            {upcomingGigs.map(gig => (
              <Link key={gig.id} to="/gig/catalogue" className="upcoming-item">
                <span>
                  <strong>{gig.name}</strong>
                  <small>
                    {gig.scheduled_date
                      ? new Date(gig.scheduled_date).toLocaleDateString('en-GB', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'Date TBD'}
                    {gig.venue ? ` at ${gig.venue}` : ''}
                  </small>
                </span>
                <em>
                  {parseInt(gig.queue_count) > 0
                    ? `${gig.queue_count} requested`
                    : 'Request a song'}
                </em>
              </Link>
            ))}
          </div>
        </section>
      )}

      <footer className="site-footer">
        <span>Live music. Real vocals. Your turn.</span>
        <div className="site-footer__links">
          <a href="https://www.instagram.com/rodeoke/" target="_blank" rel="noreferrer">
            Instagram
          </a>
          <Link to={isLoggedIn ? landingPage : '/gig/login'}>
            {isLoggedIn ? 'My Page' : 'Login'}
          </Link>
        </div>
      </footer>
    </main>
  );
};

export default GigHomePage;
